/**
 * E05-F06-T11 — one recipient field (To / CC / BCC) of the send dialog.
 *
 * The value stays the comma-separated string the dialog and the api already use
 * (`parseRecipients` / `addRecipient` keep working unchanged); what this adds is
 * that every parsed address is shown SEPARATELY as a chip under the input, with
 * its own remove button and an invalid-address mark. Ksenia could not tell from a
 * bare text box that several addresses were allowed, or which of them were typos.
 *
 * Test ids: `<testId>` is the input, `<testId>-chip` each address
 * (`data-email`, `data-valid`), `<testId>-chip-remove` its remove button.
 */
import type { JSX } from 'react';
import { isValidEmail, parseRecipients } from '@eer/shared';

const FIELD_CLASS =
  'w-full rounded-md border border-[var(--color-border)] p-2 text-sm outline-none focus:border-[var(--color-accent)]';

export interface RecipientFieldProps {
  readonly label: string;
  readonly testId: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
}

export function RecipientField({
  label,
  testId,
  value,
  onChange,
  placeholder = 'name@example.com, other@example.com',
}: RecipientFieldProps): JSX.Element {
  const recipients = parseRecipients(value);

  function remove(index: number): void {
    onChange(recipients.filter((_, i) => i !== index).join(', '));
  }

  return (
    <div className="flex flex-col gap-1 text-sm">
      <label className="flex flex-col gap-1">
        <span className="font-medium text-[var(--color-text-strong)]">
          {label}
          <span className="ml-2 text-xs font-normal text-[var(--color-text-muted)]">
            several addresses — separate with commas
          </span>
        </span>
        <input
          type="text"
          data-testid={testId}
          className={FIELD_CLASS}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      </label>
      {recipients.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-chips`}>
          {recipients.map((email, i) => {
            const valid = isValidEmail(email);
            return (
              <span
                key={`${email}-${i}`}
                data-testid={`${testId}-chip`}
                data-email={email}
                data-valid={valid ? 'true' : 'false'}
                title={valid ? email : 'Not a valid email address'}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs ${
                  valid
                    ? 'border-[var(--color-border)] bg-[var(--color-surface-muted)] text-[var(--color-text-strong)]'
                    : 'border-red-400 bg-red-50 text-red-700'
                }`}
              >
                {email}
                <button
                  type="button"
                  data-testid={`${testId}-chip-remove`}
                  aria-label={`Remove ${email}`}
                  onClick={() => remove(i)}
                  className="leading-none text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)]"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default RecipientField;
